<template>
  <div v-if="isDisplayed" class="modal-bg">
    <div
      ref="modalEl"
      v-hotkey="shortcuts"
      v-click-outside="onClickOutside"
      class="modal"
      tabindex="0"
    >
      <div class="modal-wrapper">
        <div class="header">
          <slot name="header" />
        </div>

        <div class="title">
          <slot name="title" />
        </div>

        <div class="actions">
          <slot name="actions" />
          <CloseButton
            class="close-icon"
            :shortcut="formActions.cancel.shortcutsDisplay"
            @click="onClose"
          />
        </div>
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  PropType,
  ref,
  toRefs,
  watch,
} from "vue";

import { formActions } from "@/components/actions/actions";
import CloseButton from "@/components/elements/CloseButton.vue";
import * as inject from "@/components/inject";
import {
  closeModal,
  ShortcutList,
  undo,
  useModalOrInputShortcuts,
} from "@/components/shortcuts";

export default defineComponent({
  name: "Modal",
  components: {
    CloseButton,
  },
  props: {
    isDisplayed: { type: Boolean, required: true },
    modalShortcuts: { type: Array as PropType<ShortcutList>, required: false },
  },
  emits: ["close", "clickoutside"],
  setup(props, { emit }) {
    const { modalShortcuts, isDisplayed } = toRefs(props);
    const isModalShown = inject.isModalShown();
    const modalEl = ref<HTMLElement>();
    watch(isDisplayed, (v) => {
      isModalShown.value = v;
      nextTick(() => {
        if (v) modalEl.value?.focus();
      });
    });
    onBeforeUnmount(() => (isModalShown.value = false));
    const onClose = () => {
      emit("close");
      isModalShown.value = false;
    };
    const onClickOutside = () => {
      emit("clickoutside");
    };
    const shortcutList = computed<ShortcutList>(() => {
      const defaultShortcuts: ShortcutList = [
        { keys: closeModal, action: onClose },
        { keys: undo, action: () => {} },
        { keys: ref("tab"), action: () => {} },
      ];
      if (modalShortcuts.value)
        return [...defaultShortcuts, ...modalShortcuts.value];
      else return defaultShortcuts;
    });
    const areShortcutsEnabled = computed(
      () => isModalShown.value && isDisplayed.value
    );
    const { shortcuts } = useModalOrInputShortcuts(
      shortcutList,
      areShortcutsEnabled
    );
    return {
      modalEl,
      onClickOutside,
      onClose,
      shortcuts,
      formActions,
    };
  },
});
</script>

<style scoped lang="scss">
.modal-bg {
  .modal {
    position: absolute;
    top: 50%;
    inset-inline-start: 50%;
    transform: translate(-50%, -50%);
    margin: auto;
    min-height: 100px;
    max-height: 80vh;
    overflow: auto;
    min-width: 350px;
    border-radius: 10px;
    box-shadow: $console;
    padding: 24px;
    @extend %font-body-regular;
    @include themed() {
      background: t($bg);
      color: t($primary);
    }

    .modal-wrapper {
      position: relative;
      display: flex;
      flex-direction: column;

      .header {
        position: absolute;
        top: 0;
        inset-inline-start: 0;
        inset-inline-end: 0;
        letter-spacing: 1px;
        text-transform: uppercase;
        @extend %font-small-regular;
        @include themed() {
          color: t($grey-50);
        }
      }

      .title {
        padding-bottom: 24px;
      }

      .actions {
        z-index: $z-index-content-elevated;
        position: absolute;
        top: 0;
        inset-inline-end: 0;
        display: flex;

        .close-icon {
          margin-inline-start: 24px;
        }
      }
    }
  }
}
</style>
